window.Eurus = window.Eurus || {};
window.Eurus.loadedScript = window.Eurus.loadedScript || new Set();

if (!window.Eurus.loadedScript.has('description-disclosure.js')) {
  window.Eurus.loadedScript.add('description-disclosure.js');

  (() => {
    const ROOT_SELECTOR = '[data-description-disclosure]';
    const CONTENT_SELECTOR = '[data-description-content]';
    const WRAP_SELECTOR = '[data-description-wrap]';
    const TOGGLE_SELECTOR = '[data-description-toggle]';
    const EXPANSION_CONTAINER_SELECTOR = '[data-description-expansion-container]';
    const SHELL_SELECTOR = '[data-description-shell]';
    const BODY_SELECTOR = '[data-description-body]';
    const MINIMUM_SELECTOR = '[data-description-minimum]';
    const states = new WeakMap();
    let descriptionId = 0;
    let resizeFrame = null;

    const expansionContainer = root => root.closest(EXPANSION_CONTAINER_SELECTOR);
    const isScrollContainer = container => container?.dataset.descriptionExpansion === 'scroll';
    const styleOf = element => (typeof getComputedStyle === 'function' && element ? getComputedStyle(element) : null);

    // The in-flow shell already fits the collapsed content. Expansion uses only
    // its remaining space, keeping the banner and CTA anchored. Subtract the
    // current wrap height so remeasurement while expanded uses the same base.
    const applyScrollHeight = (root, state) => {
      const container = expansionContainer(root);
      if (!container || !isScrollContainer(container)) return;

      const wrap = state?.wrap || root.querySelector(WRAP_SELECTOR);
      if (!wrap) return;

      const collapsed = state?.collapsedHeight || wrap.getBoundingClientRect().height;
      const lineHeight = state?.lineHeight || 24;
      const expandedRows = Math.max(1, Number.parseInt(root.dataset.descriptionExpandedRows, 10) || 6);

      const shell = container.querySelector(SHELL_SELECTOR);
      const body = container.querySelector(BODY_SELECTOR);
      if (!shell || !body) {
        root.style.setProperty('--description-scroll-max-height', `${Math.round(collapsed)}px`);
        return;
      }

      const shellStyle = styleOf(shell);
      const paddingTop = Number.parseFloat(shellStyle?.paddingTop) || 0;
      const paddingBottom = Number.parseFloat(shellStyle?.paddingBottom) || 0;
      const shellInner = shell.getBoundingClientRect().height - paddingTop - paddingBottom;
      const bodyHeight = body.getBoundingClientRect().height;
      const wrapHeight = wrap.getBoundingClientRect().height;
      const baseBody = bodyHeight - wrapHeight + collapsed;
      // A responsive resize can make the expanded copy itself enlarge the grid
      // row. Use its independent spacer, not that inflated shell, as the target.
      const minimum = container.querySelector(MINIMUM_SELECTOR);
      const minimumInner = minimum
        ? minimum.getBoundingClientRect().height - paddingTop - paddingBottom
        : shellInner;
      const free = Math.max(0, minimumInner - baseBody - 8);
      const maxHeight = Math.max(collapsed, collapsed + Math.min(free, expandedRows * lineHeight));
      root.style.setProperty('--description-scroll-max-height', `${Math.round(maxHeight)}px`);
    };

    const updateScrollFocus = (root, state) => {
      if (!state.expanded || !isScrollContainer(expansionContainer(root))) return;
      if (state.wrap.scrollHeight > state.wrap.clientHeight + 1) {
        state.wrap.setAttribute('tabindex', '0');
      } else {
        state.wrap.removeAttribute('tabindex');
      }
    };

    const setExpansionContainer = (root, expanded, state = states.get(root)) => {
      const container = expansionContainer(root);
      if (!container) return;

      if (isScrollContainer(container)) {
        container.classList.toggle('description-disclosure-scroll', expanded);
        const wrap = state?.wrap || root.querySelector(WRAP_SELECTOR);
        if (expanded) {
          applyScrollHeight(root, state);
          if (wrap) {
            wrap.setAttribute('tabindex', '0');
            requestAnimationFrame(() => {
              if (!wrap.isConnected) return;
              updateScrollFocus(root, state);
            });
          }
        } else if (wrap) {
          wrap.removeAttribute('tabindex');
          wrap.scrollTop = 0;
          root.style.removeProperty('--description-scroll-max-height');
        }
        return;
      }

      if (expanded) {
        container.style.setProperty('--description-expanded-min-height', `${container.getBoundingClientRect().height}px`);
        container.classList.add('description-disclosure-expanded');
      } else {
        container.classList.remove('description-disclosure-expanded');
        container.style.removeProperty('--description-expanded-min-height');
      }
    };

    const setExpanded = (root, expanded) => {
      const state = states.get(root);
      if (!state || !state.canTruncate) return;

      const container = expansionContainer(root);
      if (expanded && container && isScrollContainer(container)) {
        // Measure before the wrap expands so the first paint never jumps.
        applyScrollHeight(root, state);
      }

      state.expanded = expanded;
      root.classList.toggle('is-expanded', expanded);
      state.wrap.classList.toggle('is-collapsed', !expanded);
      state.toggle.setAttribute('aria-expanded', String(expanded));
      state.more.hidden = expanded;
      state.less.hidden = !expanded;
      setExpansionContainer(root, expanded, state);
    };

    const measure = root => {
      const state = states.get(root);
      if (!state || !root.isConnected) return;

      const width = state.content.getBoundingClientRect().width;
      if (width <= 0) return;

      // Zero means inherit the desktop count, or preserve a legacy preview.
      const responsiveLines = window.matchMedia?.('(max-width: 767px)').matches
        ? root.dataset.descriptionLinesMobile
        : root.dataset.descriptionLinesDesktop;
      state.lines = Math.max(1, Number.parseInt(responsiveLines, 10) || Number.parseInt(root.dataset.descriptionLines, 10) || 2);

      const clone = state.content.cloneNode(true);
      clone.removeAttribute('id');
      clone.removeAttribute('data-description-content');
      clone.setAttribute('data-description-measurement', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
      Object.assign(clone.style, {
        position: 'absolute',
        visibility: 'hidden',
        pointerEvents: 'none',
        zIndex: '-1',
        top: '0',
        left: '0',
        width: `${width}px`,
        height: 'auto',
        maxHeight: 'none',
        display: '-webkit-box',
        overflow: 'hidden',
        webkitBoxOrient: 'vertical',
        webkitLineClamp: String(state.lines),
        lineClamp: String(state.lines)
      });
      state.wrap.append(clone);

      const clampHeight = lines => {
        clone.style.webkitLineClamp = String(lines);
        clone.style.lineClamp = String(lines);
        return clone.getBoundingClientRect().height;
      };
      const explicitLines = Number.parseInt(responsiveLines, 10) || 0;
      if (!explicitLines && (root.dataset.descriptionLegacyPreset || root.dataset.descriptionLegacyExcerpt)) {
        let budget;
        if (root.dataset.descriptionLegacyExcerpt) {
          const excerpt = clone.cloneNode(false);
          excerpt.textContent = root.dataset.descriptionLegacyExcerpt;
          excerpt.style.display = 'block';
          excerpt.style.webkitLineClamp = 'unset';
          excerpt.style.lineClamp = 'unset';
          state.wrap.append(excerpt);
          budget = excerpt.getBoundingClientRect().height;
          excerpt.remove();
        } else {
          const desktop = window.matchMedia?.('(min-width: 1024px)').matches;
          const heights = { small: [50, 70], medium: [90, 100], large: [130, 150] };
          budget = (heights[root.dataset.descriptionLegacyPreset] || heights.small)[desktop ? 1 : 0];
        }
        // Search rendered clamp heights, including paragraph/list spacing,
        // rather than assuming every rich-text line has the same metrics.
        clone.style.display = '-webkit-box';
        clone.style.webkitLineClamp = 'unset';
        clone.style.lineClamp = 'unset';
        const full = clone.getBoundingClientRect().height;
        const lineHeight = Number.parseFloat(styleOf(state.content)?.lineHeight) || 16;
        let low = 1;
        let high = Math.max(1, Math.ceil(full / Math.max(1, lineHeight)) + 1);
        if (full <= budget + 1) {
          state.lines = high;
        } else {
          while (low < high) {
            const middle = Math.ceil((low + high) / 2);
            if (clampHeight(middle) <= budget + 1) low = middle;
            else high = middle - 1;
          }
          state.lines = low;
        }
      }
      clampHeight(state.lines);
      root.style.setProperty('--description-lines', String(state.lines));

      const collapsedHeight = clone.getBoundingClientRect().height;
      // Keep the same formatting context: switching to block can collapse
      // paragraph margins and incorrectly classify a one-line overflow as fit.
      clone.style.display = '-webkit-box';
      clone.style.overflow = 'visible';
      clone.style.webkitLineClamp = 'unset';
      clone.style.lineClamp = 'unset';
      const fullHeight = clone.getBoundingClientRect().height;
      clone.remove();

      state.collapsedHeight = collapsedHeight;
      const computedLineHeight = Number.parseFloat(styleOf(state.content)?.lineHeight);
      state.lineHeight = Number.isFinite(computedLineHeight) && computedLineHeight > 0
        ? computedLineHeight
        : (state.lines > 0 ? collapsedHeight / state.lines : 24);

      state.canTruncate = fullHeight > collapsedHeight + 1;
      state.wrap.classList.toggle('can-truncate', state.canTruncate);
      root.classList.toggle('can-truncate', state.canTruncate);
      state.toggle.hidden = !state.canTruncate;

      if (!state.canTruncate) {
        state.expanded = false;
        root.classList.remove('is-expanded');
        state.wrap.classList.remove('is-collapsed');
        state.toggle.setAttribute('aria-expanded', 'false');
        state.more.hidden = false;
        state.less.hidden = true;
        setExpansionContainer(root, false, state);
        return;
      }

      state.wrap.classList.toggle('is-collapsed', !state.expanded);
      if (state.expanded) {
        applyScrollHeight(root, state);
        updateScrollFocus(root, state);
      }
    };

    const queueMeasure = root => {
      const state = states.get(root);
      if (!state) return;
      if (state.measureFrame) cancelAnimationFrame(state.measureFrame);
      state.measureFrame = requestAnimationFrame(() => {
        state.measureFrame = null;
        measure(root);
      });
    };

    const initialize = root => {
      const previous = states.get(root);
      if (previous) {
        if (previous.content === root.querySelector(CONTENT_SELECTOR) && previous.wrap === root.querySelector(WRAP_SELECTOR) && previous.toggle === root.querySelector(TOGGLE_SELECTOR)) return;
        cleanupWithin(root);
      }

      const content = root.querySelector(CONTENT_SELECTOR);
      const wrap = root.querySelector(WRAP_SELECTOR);
      const toggle = root.querySelector(TOGGLE_SELECTOR);
      const more = toggle?.querySelector('[data-description-more]');
      const less = toggle?.querySelector('[data-description-less]');
      if (!content || !wrap || !toggle || !more || !less) return;

      const lines = Math.max(1, Number.parseInt(root.dataset.descriptionLines, 10) || 2);
      const id = `description-disclosure-${++descriptionId}`;
      content.id = id;
      toggle.setAttribute('aria-controls', id);

      const state = {
        content,
        wrap,
        toggle,
        more,
        less,
        lines,
        collapsedHeight: 0,
        lineHeight: 0,
        expanded: false,
        canTruncate: false,
        measureFrame: null,
        resizeObserver: null
      };
      states.set(root, state);
      root.classList.remove('is-expanded');
      wrap.classList.add('is-collapsed');
      toggle.setAttribute('aria-expanded', 'false');
      more.hidden = false;
      less.hidden = true;

      if ('ResizeObserver' in window) {
        state.resizeObserver = new ResizeObserver(() => queueMeasure(root));
        state.resizeObserver.observe(root);
        const container = expansionContainer(root);
        if (isScrollContainer(container)) {
          const shell = container.querySelector(SHELL_SELECTOR);
          const body = container.querySelector(BODY_SELECTOR);
          if (shell) state.resizeObserver.observe(shell);
          if (body) state.resizeObserver.observe(body);
        }
      }

      queueMeasure(root);
    };

    const initializeWithin = scope => {
      if (!scope) return;
      if (scope.matches?.(ROOT_SELECTOR)) initialize(scope);
      scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
    };

    const cleanupWithin = scope => {
      if (!scope) return;
      const roots = [];
      if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
      scope.querySelectorAll?.(ROOT_SELECTOR).forEach(root => roots.push(root));
      roots.forEach(root => {
        const state = states.get(root);
        state?.resizeObserver?.disconnect();
        if (state?.measureFrame) cancelAnimationFrame(state.measureFrame);
        setExpansionContainer(root, false, state);
        states.delete(root);
      });
    };

    document.addEventListener('click', event => {
      const toggle = event.target.closest?.(TOGGLE_SELECTOR);
      if (!toggle) return;
      const root = toggle.closest(ROOT_SELECTOR);
      const state = states.get(root);
      if (!state?.canTruncate) return;

      const expanded = !state.expanded;
      setExpanded(root, expanded);
      if (!expanded) toggle.scrollIntoView({ block: 'nearest' });
    });

    document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
    document.addEventListener('shopify:section:unload', event => cleanupWithin(event.target));
    document.addEventListener('description-disclosure:refresh', event => {
      initializeWithin(event.target);
      if (event.target.matches?.(ROOT_SELECTOR)) queueMeasure(event.target);
      event.target.querySelectorAll?.(ROOT_SELECTOR).forEach(queueMeasure);
    });

    const mutationObserver = new MutationObserver(records => {
      records.forEach(record => {
        const element = record.target?.nodeType === 3 ? record.target.parentElement : record.target;
        // Measurement clones are transient and must never schedule themselves.
        if (element?.closest?.('[data-description-measurement]')) return;
        const content = element?.closest?.(CONTENT_SELECTOR);
        const root = content?.closest(ROOT_SELECTOR);
        if (root) queueMeasure(root);
        record.removedNodes?.forEach(node => {
          if (!node.isConnected) cleanupWithin(node);
        });
        record.addedNodes?.forEach(node => {
          if (node.matches?.('[data-description-measurement]')) return;
          initializeWithin(node);
          const ancestor = element?.closest?.(ROOT_SELECTOR);
          if (ancestor) {
            initialize(ancestor);
            queueMeasure(ancestor);
          }
        });
      });
    });
    mutationObserver.observe(document.documentElement, { childList: true, characterData: true, subtree: true });

    window.addEventListener('resize', () => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = null;
        document.querySelectorAll(ROOT_SELECTOR).forEach(queueMeasure);
      });
    });

    document.fonts?.ready?.then(() => {
      document.querySelectorAll(ROOT_SELECTOR).forEach(queueMeasure);
    });
    document.fonts?.addEventListener?.('loadingdone', () => {
      document.querySelectorAll(ROOT_SELECTOR).forEach(queueMeasure);
    });

    initializeWithin(document);
  })();
}
